import React, { useState, useCallback } from 'react';
import { ExecutionTemplate, ExecutionPipelinesApiClient } from 'process-pipelines-common';
import { BasicsTab } from '../tabs/BasicsTab';
import { JsonTab } from '../tabs/JsonTab';
import { DataItemsTab } from '../tabs/DataItemsTab';
import { RenderOptionsTab } from '../tabs/RenderOptionsTab';

export interface TemplateEditorProps {
  template: ExecutionTemplate;
  apiClient: ExecutionPipelinesApiClient;
  onSave?: (template: ExecutionTemplate) => void | Promise<void>;
  onClose?: () => void;
  readOnly?: boolean;
  readOnlyScope?: boolean; // When creating from Studio, scope is read-only
  isCreating?: boolean; // If true, will call createExecutionTemplate instead of update
  initialTab?: TabType; // Start on a specific tab
  hideCloseButton?: boolean; // Hide Close button when used inside TemplateEditorModal
}

export type TabType = 'basics' | 'json' | 'dataItems' | 'renderOptions';

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template: initialTemplate,
  apiClient,
  onSave,
  onClose,
  readOnly = false,
  readOnlyScope = false,
  isCreating = false,
  initialTab = 'basics',
  hideCloseButton = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [template, setTemplate] = useState(initialTemplate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const handleTemplateChange = useCallback(
    (updates: Partial<ExecutionTemplate>) => {
      setTemplate((prev) => ({ ...prev, ...updates }));
      setIsDirty(true);
    },
    []
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Validate required fields
      const errors: string[] = [];

      if (!template.name || !template.name.trim()) {
        errors.push('• Name is required');
      }

      if (!template.slug || !template.slug.trim()) {
        errors.push('• Slug is required');
      }

      // Accept 'Allow' or 'Block' as valid values
      if (template.concurrencyBehavior !== 'Allow' && template.concurrencyBehavior !== 'Block') {
        errors.push('• Concurrency Behavior is required');
      }

      // Required field. BasicsTab always seeds a default ('Execute') the moment it mounts, so an
      // empty value here means the field was explicitly cleared out from under it (e.g. via the
      // Json tab) rather than never set.
      if (template.defaultTriggerMode == null || template.defaultTriggerMode === '') {
        errors.push('• Default Trigger Mode is required');
      }

      if (errors.length > 0) {
        setSaving(false);
        setError(`Please fill in the following mandatory fields:\n${errors.join('\n')}`);
        return;
      }

      // Validation passed - let parent component handle API call. onSave may be
      // async (TemplateEditorModal's handleSave is), so it must be awaited here
      // -- otherwise a failed save leaves this button stuck disabled on
      // "Saving..." forever, since nothing else ever clears `saving` and the
      // modal that owns onSave does not unmount on error.
      setIsDirty(false);
      setError(null);
      await onSave?.(template);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save template';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [template, onSave]);

  const handleDiscard = useCallback(() => {
    setTemplate(initialTemplate);
    setIsDirty(false);
  }, [initialTemplate]);

  const tabs: { id: TabType; label: string; component: React.ReactNode }[] = [
    { id: 'basics', label: 'Basics', component: <BasicsTab template={template} onChange={handleTemplateChange} readOnly={readOnly} readOnlyScope={readOnlyScope} apiClient={apiClient} /> },
    { id: 'dataItems', label: 'Data Items', component: <DataItemsTab template={template} onChange={handleTemplateChange} readOnly={readOnly} /> },
    { id: 'renderOptions', label: 'Render Options', component: <RenderOptionsTab template={template} onChange={handleTemplateChange} readOnly={readOnly} /> },
    { id: 'json', label: 'Json', component: <JsonTab template={template} onChange={handleTemplateChange} readOnly={readOnly} /> },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {/* Only show title when not used inside TemplateEditorModal (modal has its own title) */}
        {!hideCloseButton && (
          <div style={styles.title}>
            <h2 style={styles.h2}>{isCreating ? 'Create' : 'Edit'} Template: {template.name || '(Unsaved)'}</h2>
          </div>
        )}
        <div style={styles.actions}>
          {isDirty && !readOnly && (
            <>
              <button
                onClick={handleDiscard}
                className="template-editor-discard-btn"
                style={styles.discardButton}
                disabled={saving}
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                className="template-editor-save-btn"
                style={styles.saveButton}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
          {!hideCloseButton && (
            <button
              onClick={() => {
                if (isDirty) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                    onClose?.();
                  }
                } else {
                  onClose?.();
                }
              }}
              className="template-editor-close-btn"
              style={styles.closeButton}
              disabled={saving}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className="template-editor-tab"
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab.id ? styles.tabButtonActive : styles.tabButtonInactive),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.tabContent}>
        {tabs.find((t) => t.id === activeTab)?.component}
      </div>

      {isDirty && (
        <div style={styles.dirtySectionBanner}>
          You have unsaved changes. Click "Save Changes" to persist them.
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    backgroundColor: 'transparent',
  },
  title: {
    flex: 1,
  },
  h2: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
  },
  discardButton: {
    padding: '6px 12px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '0',
    cursor: 'pointer',
    fontSize: '12px',
  },
  saveButton: {
    padding: '6px 12px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    fontSize: '12px',
  },
  closeButton: {
    padding: '6px 12px',
    backgroundColor: '#e0e0e0',
    border: '1px solid #999',
    borderRadius: '0',
    cursor: 'pointer',
    fontSize: '12px',
  },
  errorBanner: {
    padding: '16px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    fontSize: '13px',
    borderBottom: '2px solid #ef4444',
    borderRadius: '0',
    margin: '12px 16px',
    whiteSpace: 'pre-line' as const,
    lineHeight: '1.6',
    fontWeight: '500',
  },
  tabBar: {
    display: 'flex',
    gap: '0',
    width: '100%',
    borderBottom: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    backgroundColor: 'var(--bg-secondary, rgb(15, 23, 42))',
    overflow: 'visible' as const,
  },
  tabButton: {
    flex: 1,
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    whiteSpace: 'normal' as const,
    textAlign: 'center' as const,
  },
  tabButtonActive: {
    borderBottomColor: 'var(--primary-color, #30a046)',
    color: 'var(--primary-color, #30a046)',
    backgroundColor: 'transparent',
  },
  tabButtonInactive: {
    borderBottomColor: 'transparent',
    color: 'var(--text-secondary, #9ca3af)',
  },
  tabContent: {
    flex: 1,
    overflow: 'auto' as const,
  },
  dirtySectionBanner: {
    padding: '8px 16px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    fontSize: '12px',
    borderTop: '1px solid #ffc107',
  },
} as const;
